<template>
  <div>
    <toastification-loading-content ref="loadingToast" />
    <toastification-progress-content ref="progressToast" />
    <bosch-panels-wizard
      :is-active.sync="isWizardActive"
      :item-edit.sync="itemEdit"
      :is-add.sync="isAdd"
      :facilities-options="facilitiesOptions"
      :center-options="centerOptions"
      :sector-options="sectorOptions"
      :reseller-options="resellerOptions"
      @close-wizard="closeWizard"
      @refetch-data="refetchData"
    />
    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters
          :search-query.sync="searchQuery"
          :facilities="facilitiesOptions"
          :facilities-filter.sync="facilitiesFilter"
          :centers="centerOptions"
          :center-filter.sync="centerFilter"
          :sectors="sectorOptions"
          :sector-filter.sync="sectorFilter"
          :resellers="resellerOptions"
          :reseller-filter.sync="resellerFilter"
        />
      </template>
    </filter-modal>

    <!-- Table Container Card -->
    <b-card class="mx-2" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="text-capitalize">{{ $t("table.show") }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-1"
            />
            <label class="text-capitalize">{{ $t("table.entries") }}</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('table.search')"
              />
              <b-button
                variant="primary"
                @click="openAdd"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                v-if="$can('create', 'BoschPanel')"
              >
                <span class="text-nowrap text-capitalize"
                  >{{ $t("actions.add") }} {{ $t("bosch.panel") }}</span
                >
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        :sort-desc.sync="isSortDirDesc"
        show-empty
        :empty-text="$t('no_record_found')"
      >
        <!-- Header templates for translations -->
        <template #head()="data">
          <span>{{ $tc(data.label, 1) }}</span>
        </template>

        <!-- Column: Panel Status -->
        <template #cell(panel_status)="data">
          <div
            :style="`background: ${isOnline(data.item) ? 'green' : 'red'};
                width: 1.5rem;
                height: 1.5rem;
                border-radius: 1rem;
                text-align: center;`"
            :title="$t(`bosch.${isOnline(data.item) ? 'connected' : 'disconnected'}`)"
          ></div>
        </template>

        <!-- Column: Active -->
        <!--<template #cell(active)="data">
          <div class="text-center">
            <button
              v-if="!data.item.active && $can('arm_panel', 'BoschPanel')"
              class="btn btn-sm btn-success"
              :disabled="loading"
              @click="armPanel(data.item)"
            >
              {{ $t("bosch.armPanel") }}
            </button>
            <button
              v-if="data.item.active && $can('disarm_panel', 'BoschPanel')"
              class="btn btn-sm btn-orange"
              :disabled="loading"
              @click="armPanel(data.item)"
            >
              {{ $t("bosch.disarmPanel") }}
            </button>
          </div>
        </template>-->

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-button
            :id="`actions-${data.item.id}`"
            tabindex="0"
            variant="white"
            class="px-1 btn-y"
          >
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </b-button>
          <b-popover
            :target="`actions-${data.item.id}`"
            triggers="focus"
            placement="bottom"
            custom-class="popover-p-0"
            variant="white"
          >
            <b-list-group class="m-0">
              <b-list-group-item
                class="border-0"
                :to="`/bosch/panels/${data.item.id}/devices`"
              >
                <feather-icon icon="HardDriveIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("bosch.devices.devices") }}
                </span>
              </b-list-group-item>

              <!-- Edit -->
              <b-list-group-item
                v-if="$can('update', 'BoschPanel')"
                class="border-0"
                button
                @click="openEdit(data.item)"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50 text-capitalize">{{ $t("actions.edit") }}</span>
              </b-list-group-item>

              <!-- Delete -->
              <b-list-group-item
                v-if="$can('delete', 'BoschPanel')"
                class="border-0"
                button
                @click="confirmDelete(data.item.id)"
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50 text-capitalize">{{ $t("actions.delete") }}</span>
              </b-list-group-item>
            </b-list-group>
          </b-popover>
        </template>

        <!-- Column: Name -->
        <template #cell(name)="data">
          <b-media vertical-align="center">
            <span class="font-weight-bold d-block text-nowrap">
              {{ data.item.name }}
            </span>
            <small class="text-muted">{{ data.item.uuid }}</small>
          </b-media>
        </template>

        <!-- Column: Panel Host -->
        <template #cell(panel_host)="data">
          <span class="text-nowrap">
            {{ data.item.panel_host }}
          </span>
        </template>

        <!-- Column: Panel Passcode -->
        <template #cell(panel_passcode)="data">
          <div
            style="cursor: pointer"
            @click="copyToClipboard(data.item.id, 'panel_passcode')"
          >
            <feather-icon icon="CopyIcon" size="16" class="align-middle text-body" />
          </div>
        </template>

        <!-- Column: Panel Model -->
        <template #cell(panel_model)="data">
          <span class="text-nowrap">
            {{ data.item.panel_model || "-" }}
          </span>
        </template>

        <!-- Column: Panel Instance -->
        <template #cell(panel_instance)="data">
          <span class="text-nowrap">
            {{ data.item.panel_instance || "-" }}
          </span>
        </template>

        <!-- Column: Units -->
        <template #cell(units)="data">
          {{ unitsSite(data.item) }}
        </template>

        <!-- Column: Center -->
        <template #cell(center)="data">
          <span>{{
            (centerOptions.find((c) => c.value === data.item.id_center) || {}).label ||
            "-"
          }}</span>
        </template>

        <!-- Column: Sector -->
        <template #cell(sector)="data">
          <span>{{
            (sectorOptions.find((s) => s.value === data.item.id_sector) || {}).label ||
            "-"
          }}</span>
        </template>
      </b-table>

      <!-- Footer Table -->
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted text-capitalize">{{
              $t("table.showing", {
                from: dataMeta.from,
                to: dataMeta.to,
                of: dataMeta.of,
              })
            }}</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <Pagination
              :totalList="totalList"
              :currentPage="currentPage"
              @updatePagination="update"
              :perPage="perPage"
            />
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BPagination,
  BInputGroup,
  BMedia,
  BBadge,
  BAvatar,
  BPopover,
  BListGroup,
  BListGroupItem,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref } from "@vue/composition-api";
import useList from "./useList";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import ToastificationProgressContent from "@core/components/toastification/ToastificationProgressContent.vue";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import Pagination from "@/components/Pagination.vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import Filters from "./Filters.vue";
import BoschPanelsWizard from "./bosch_panels_wizard/Index.vue";
import Ripple from "vue-ripple-directive";

export default {
  components: {
    Pagination,
    BoschPanelsWizard,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BPagination,
    BInputGroup,
    BMedia,
    BBadge,
    BAvatar,
    BPopover,
    BListGroup,
    BListGroupItem,
    vSelect,
    ToastificationLoadingContent,
    ToastificationProgressContent,
    BreadcrumbMenu,
    FilterModal,
    ButtonFilters,
    Filters,
  },
  directives: {
    Ripple,
  },

  setup(_, context) {
    const update = (data) => {
      currentPage.value = data;
    };

    const loadingToast = ref(null);
    const isWizardActive = ref(false);
    const itemEdit = ref({});
    const isAdd = ref(false);
    const loading = ref(false);

    const { t } = useI18nUtils();
    const toast = useToast();

    const trans = {
      "messages.delete.title": t("messages.delete.title"),
      "messages.delete.body": t("messages.delete.body"),
      "actions.delete": t("actions.delete"),
      "actions.cancel": t("actions.cancel"),
      "bosch.actions": t("bosch.actions"),
      "bosch.panel": t("bosch.panel"),
      "bosch.name": t("bosch.name"),
      "bosch.panelHost": t("bosch.panelHost"),
      "bosch.panelPasscode": t("bosch.panelPasscode"),
      "bosch.panelInstance": t("bosch.panelInstance"),
      "bosch.panelModel": t("bosch.panelModel"),
      "bosch.units": t("bosch.units"),
      "bosch.center": t("bosch.center"),
      "bosch.sector": t("bosch.sector"),
      "bosch.reseller": t("bosch.reseller"),
      "bosch.resellers": t("bosch.resellers"),
      "bosch.status": t("bosch.status"),
      "bosch.active": t("bosch.active"),
      "bosch.actions": t("bosch.actions"),
      "bosch.syncStatus": t("bosch.syncStatus"),
      "bosch.syncActions": t("bosch.syncActions"),
      "bosch.syncDevices": t("bosch.syncDevices"),
      "bosch.copyPassword": t("bosch.copyPassword"),
      "bosch.syncing": t("bosch.syncing"),
      "bosch.syncCorrectly": t("bosch.syncCorrectly"),
      "bosch.connected": t("bosch.connected"),
      "bosch.disconnected": t("bosch.disconnected"),
      "bosch.armPanel": t("bosch.armPanel"),
      "bosch.disarmPanel": t("bosch.disarmPanel"),
      "bosch.arming": t("bosch.arming"),
      "bosch.disarming": t("bosch.disarming"),
      "bosch.armedCorrectly": t("bosch.armedCorrectly"),
      "bosch.disarmedCorrectly": t("bosch.disarmedCorrectly"),
      "messages.copy.copied_successfully": t("messages.copy.copied_successfully"),
      "messages.copy.could_not_copy": t("messages.copy.could_not_copy"),
      "bosch.devices.devices": t("bosch.devices.devices"),
    };

    const {
      // methods
      fetchList,
      refetchData,
      clearSearchFilters,
      buildFilters,

      //table pagination
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,

      // filters
      centerFilter,
      sectorFilter,
      facilitiesFilter,
      resellerFilter,

      // Options
      facilitiesOptions,
      centerOptions,
      sectorOptions,
      resellerOptions,

      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      filtersCount,
    } = useList();

    const openEdit = (item) => {
      isWizardActive.value = true;
      itemEdit.value = JSON.parse(JSON.stringify(item));
      isAdd.value = false;
    };

    const closeWizard = () => {
      isWizardActive.value = false;
      itemEdit.value = {};
      isAdd.value = false;
    };

    const confirmDelete = function (id) {
      context.root
        .$swal({
          title: trans["messages.delete.title"],
          text: trans["messages.delete.body"],
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: trans["actions.delete"],
          cancelButtonText: trans["actions.cancel"],
          customClass: {
            confirmButton: "btn btn-primary",
            cancelButton: "btn btn-outline-danger ml-1",
          },
          buttonsStyling: false,
        })
        .then((result) => {
          if (result.value) {
            store.dispatch("bosch_panels/delete", id).then(() => {
              toast({
                component: ToastificationContent,
                props: {
                  title: "Eliminado!",
                  text: "El panel ha sido eliminado.",
                  icon: "CheckIcon",
                  variant: "success",
                },
              });
              refetchData();
            });
          }
        });
    };

    const openAdd = () => {
      isAdd.value = true;
      isWizardActive.value = true;
    };

    const copyToClipboard = (id, field) => {
      store
        .dispatch("bosch_panels/copyPassword", { id, field })
        .then((response) => {
          if (navigator.clipboard && response.data) {
            navigator.clipboard.writeText(response.data.password).then(() => {
              onCopy();
            });
          }
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: trans["messages.copy.could_not_copy"],
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const syncActions = async (id) => {
      loadingToast.value = toast({
        component: ToastificationContent,
        props: {
          title: trans["bosch.syncing"],
          icon: "RefreshCwIcon",
          variant: "info",
        },
      });

      try {
        await store.dispatch("bosch_panels/syncActions", id);
        toast({
          component: ToastificationContent,
          props: {
            title: trans["bosch.syncCorrectly"],
            icon: "CheckIcon",
            variant: "success",
          },
        });
        refetchData();
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: "Error al sincronizar acciones",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    };

    const syncStatus = async (id) => {
      loadingToast.value = toast({
        component: ToastificationContent,
        props: {
          title: trans["bosch.syncing"],
          icon: "RefreshCwIcon",
          variant: "info",
        },
      });

      try {
        await store.dispatch("bosch_panels/syncStatus", id);
        toast({
          component: ToastificationContent,
          props: {
            title: trans["bosch.syncCorrectly"],
            icon: "CheckIcon",
            variant: "success",
          },
        });
        refetchData();
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: "Error al sincronizar estado",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    };

    const syncDevices = async (panel) => {
      loadingToast.value = toast({
        component: ToastificationContent,
        props: {
          title: trans["bosch.syncing"],
          icon: "RefreshCwIcon",
          variant: "info",
        },
      });

      try {
        await store.dispatch("bosch_panels/syncDevices", panel.id);
        toast({
          component: ToastificationContent,
          props: {
            title: trans["bosch.syncCorrectly"],
            icon: "CheckIcon",
            variant: "success",
          },
        });
        refetchData();
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: "Error al sincronizar dispositivos",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    };

    const armPanel = function (panel) {
      const messageAlert = panel.active
        ? `${trans["bosch.disarmPanel"]} ${panel.name}?`
        : `${trans["bosch.armPanel"]} ${panel.name}?`;

      const confirmButton = panel.active
        ? trans["bosch.disarmPanel"]
        : trans["bosch.armPanel"];

      context.root
        .$swal({
          title: messageAlert,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: confirmButton,
          cancelButtonText: trans["actions.cancel"],
          customClass: {
            confirmButton: "btn btn-primary text-capitalize",
            cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
          },
          buttonsStyling: false,
        })
        .then(async (result) => {
          if (result.value) {
            let { device } = panel;
            let params = {
              panel_id: device.id,
              action: panel.active ? "disarm" : "arm",
            };

            let descriptionToast = panel.active
              ? `${trans["bosch.disarmPanel"]} ${trans["bosch.panel"]}`
              : `${trans["bosch.armPanel"]} ${trans["bosch.panel"]}`;

            loadingToast.value = toast({
              component: ToastificationContent,
              props: {
                title: descriptionToast,
                icon: "RefreshCwIcon",
                variant: "info",
              },
            });

            loading.value = true;

            try {
              await store.dispatch("bosch_panels/armPanel", params);
              const messageToast = panel.active
                ? `${trans["bosch.panel"]} ${panel.name} ${trans["bosch.disarmedCorrectly"]}`
                : `${trans["bosch.panel"]} ${panel.name} ${trans["bosch.armedCorrectly"]}`;

              toast({
                component: ToastificationContent,
                props: {
                  title: messageToast,
                  icon: "CheckIcon",
                  variant: "success",
                },
              });
              refetchData();
            } catch (error) {
              toast({
                component: ToastificationContent,
                props: {
                  title: "Error",
                  text: "Error al cambiar el estado del panel",
                  icon: "AlertTriangleIcon",
                  variant: "danger",
                },
              });
            } finally {
              loading.value = false;
            }
          }
        });
    };

    const onCopy = () => {
      toast({
        component: ToastificationContent,
        props: {
          title: trans["messages.copy.copied_successfully"],
          icon: "CheckIcon",
          variant: "success",
        },
      });
    };

    const nameNav = ref("breadcrumbs.iot.bosch.panels");

    const unitsSite = (item) => {
      let { units } = item.device;

      if (units.length) {
        let unitsDescription = units.map((unit) => unit.description);

        return unitsDescription.join(",");
      }

      return "";
    };

    const isOnline = (item) => {
      // Check device.online (database status)
      if (item.device && item.device.online) {
        return true;
      }
      // Check devices.panel.status (real-time status)
      if (item.devices && item.devices.panel && item.devices.panel.status === "ONLINE") {
        return true;
      }
      return false;
    };

    return {
      nameNav,
      update,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,

      clearSearchFilters,
      buildFilters,
      filtersCount,

      centerFilter,
      sectorFilter,
      facilitiesFilter,
      resellerFilter,

      facilitiesOptions,
      centerOptions,
      sectorOptions,
      resellerOptions,

      loadingToast,
      isWizardActive,
      itemEdit,
      isAdd,
      loading,

      openEdit,
      closeWizard,
      confirmDelete,
      openAdd,
      refetchData,
      copyToClipboard,
      armPanel,
      syncActions,
      syncStatus,
      syncDevices,
      onCopy,
      isOnline,
      unitsSite,
    };
  },
};
</script>

<style scoped>
.btn-orange {
  background-color: orange;
  color: white;
}

.btn-orange:hover {
  box-shadow: 0 8px 25px -8px orange;
}
</style>
